import * as React from 'react'

import getConfig from 'next/config'

import { useBloomreachPageRoutes, useBloomreachProductRoutes } from '@grandvisionhq/bloomreach'
import { CommerceToolsApi } from '@grandvisionhq/commercetools'
import { ProductDetailSlotsProvider } from '@grandvisionhq/fun-products'
import { NextPage, withErrorPage } from '@grandvisionhq/next'
import { CrossSellProducts } from '@grandvisionhq/search'
import { useFeatureToggles, useIntl } from '@grandvisionhq/state'
import { compose } from '@grandvisionhq/utils'
import {
  ErrorCase,
  InitialProps,
  LegacyPage,
  Props,
} from '@grandvisionhq/www-next/pages/product-detail-legacy'

import { withCms, withLayout } from '../../cms'
import { defaultErrorProps } from '../../components/error-props'
import { settings as cartSettings, withCart } from '../../components/hocs/with-cart'
import { getDeliveryIndicatorConfig, getSpecifications } from '../../configuration'
import { getAlgoliaCrossSellConfig } from '../../util/algolia'

import '@grandvisionhq/reviews/dist/index.css'

// In a future story, this entire file will be moved to `templates/product-detail-legacy.tsx`
const ProductDetailPage: NextPage<Props, InitialProps | ErrorCase> = (props) => {
  const [_, { formatProductUrl }] = useBloomreachProductRoutes()
  const { routes } = useBloomreachPageRoutes()
  const { isCrossSellEnabled } = useFeatureToggles()
  const deliveryIndicatorConfig = getDeliveryIndicatorConfig()
  const {
    publicRuntimeConfig: { socloz },
  } = getConfig()

  const specifications = React.useMemo(
    () => getSpecifications(props.productType),
    [props.productType]
  )
  const crossSellConfig = getAlgoliaCrossSellConfig(props.productType)

  if ('statusCode' in props) {
    // If an error status code was returned, render the error page.
    return null
  }

  // We need to map appointment-book to the stores page, since it's hardcoded in core.
  const adjustedRoutes = {
    ...routes,
    'book-appointment': routes.stores,
  }

  const ReserveButton = ({ variant }: { variant?: CommerceToolsApi.SharedFrameInfo }) => {
    const { getLabel } = useIntl()
    return (
      <button
        className="button button--primary button--outline socloz"
        data-socloz-service="reservation"
        data-socloz-product-id={variant?.sku}
        data-socloz-product-id_type="ean"
        data-t="btn-reserve-in-store"
      >
        {getLabel('products.actions.reserveInStore')}
      </button>
    )
  }

  const SecondaryProductInfoSlotB = isCrossSellEnabled
    ? () => (
        <CrossSellProducts formatProductUrl={formatProductUrl} algoliaConfig={crossSellConfig} />
      )
    : undefined

  const createReserveButton = (variant?: CommerceToolsApi.SharedFrameInfo) => (
    <ReserveButton variant={variant} />
  )
  return (
    <>
      <ProductDetailSlotsProvider
        slots={{
          SecondaryProductInfoSlotB,
        }}
      >
        <LegacyPage
          {...props}
          maxOrderableQuantity={cartSettings.maximumQuantity}
          deliveryIndicatorConfig={deliveryIndicatorConfig}
          specificationConfiguration={specifications}
          adjustedPageRoutes={adjustedRoutes}
          secondaryCallToAction={createReserveButton}
        />
        <script
          defer
          id="soclozjs"
          src={socloz.srcUrl}
          data-socloz-app_key={socloz.appKey}
          data-socloz-country="FR"
          data-socloz-locale="fr_FR"
        />
      </ProductDetailSlotsProvider>
    </>
  )
}
ProductDetailPage.getInitialProps = LegacyPage.getInitialProps

export default compose<Props, Props>(
  withCms(),
  withErrorPage(defaultErrorProps),
  withLayout(),
  withCart({ readOnly: true })
)(ProductDetailPage)
